import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/** Older rows are fetched a screen at a time, matching the transcript's first page. */
const DEFAULT_SCROLL_BACK_PAGE_SIZE = 20;
/**
 * Start the fetch slightly before the top edge is reached, so the page is usually already there
 * by the time the user gets to it.
 */
const SCROLL_BACK_PREFETCH_MARGIN_PX = 240;

type ScrollAnchor = {
  scrollTop: number;
  scrollHeight: number;
  firstItemKey: string | null;
};

/**
 * Infinite scroll-back for the Claudia transcript (LAT-952).
 *
 * The transcript renders oldest-first and is pinned to the bottom, so "load more" means
 * *prepending*. Two things make that different from an ordinary infinite list:
 *
 * - **The viewport must not move.** A prepend grows the content above the reader; the browser
 *   keeps `scrollTop`, so the rows they were looking at slide down by the height of everything
 *   that arrived. The anchor is captured before the request and restored, in a layout effect,
 *   from the change in `scrollHeight` — before the browser paints, so there is no visible jump.
 * - **Only a real prepend may move it.** A streamed token arriving while a page is in flight also
 *   grows `scrollHeight`, and adjusting for that would drag the reader downwards. The restore
 *   therefore fires only when the FIRST item changed, which is what a prepend means and an append
 *   never does.
 *
 * Both Claudia surfaces (the full-screen timeline and the sidebar panel) mount this the same way:
 * `viewportRef` on the scroll container, `sentinelRef` on an empty row above the oldest item.
 */
export function useClaudiaTranscriptScrollBack(args: {
  /** The paginated message feed still has older pages. */
  canLoadMore: boolean;
  /** A page is in flight right now. */
  isLoadingMore: boolean;
  /** Rendered timeline items — identity of the first one is how a prepend is detected. */
  firstItemKey: string | null;
  loadMore: (numItems?: number) => void;
  pageSize?: number;
}) {
  // The sentinel is conditionally rendered, and the observer has to be rebuilt whenever it comes
  // back, so it is held in state behind a callback ref (`apps/web-app/AGENTS.md`).
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  // The scroll container is NOT: nothing needs to re-run when it changes — the sentinel lives
  // inside it, so a new container always brings a new sentinel with it, and the effects below read
  // the element when they run, by which time every ref in the commit is set.
  const viewportRef = useRef<HTMLElement | null>(null);
  const setViewport = useCallback((node: HTMLElement | null) => {
    viewportRef.current = node;
  }, []);
  const pendingAnchorRef = useRef<ScrollAnchor | null>(null);
  const sawLoadingRef = useRef(false);
  const sentinelVisibleRef = useRef(false);
  const { canLoadMore, isLoadingMore, firstItemKey, loadMore } = args;
  const pageSize = args.pageSize ?? DEFAULT_SCROLL_BACK_PAGE_SIZE;

  const requestOlderPage = useCallback(() => {
    if (!canLoadMore || pendingAnchorRef.current) {
      return;
    }
    const viewport = viewportRef.current;
    pendingAnchorRef.current = {
      scrollTop: viewport?.scrollTop ?? 0,
      scrollHeight: viewport?.scrollHeight ?? 0,
      firstItemKey,
    };
    sawLoadingRef.current = false;
    loadMore(pageSize);
  }, [canLoadMore, firstItemKey, loadMore, pageSize]);

  useEffect(() => {
    if (!sentinel || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) {
          return;
        }
        sentinelVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          requestOlderPage();
        }
      },
      {
        root: viewportRef.current,
        rootMargin: `${SCROLL_BACK_PREFETCH_MARGIN_PX}px 0px 0px 0px`,
      }
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [requestOlderPage, sentinel]);

  useLayoutEffect(() => {
    if (isLoadingMore) {
      sawLoadingRef.current = true;
    }
    const anchor = pendingAnchorRef.current;
    if (!anchor) {
      return;
    }

    if (anchor.firstItemKey !== firstItemKey) {
      pendingAnchorRef.current = null;
      const viewport = viewportRef.current;
      const grownBy = (viewport?.scrollHeight ?? 0) - anchor.scrollHeight;
      if (viewport && grownBy > 0) {
        viewport.scrollTop = anchor.scrollTop + grownBy;
      }
    } else if (sawLoadingRef.current && !isLoadingMore) {
      // The page settled without prepending anything — the thread is exhausted, or the window did
      // not grow. Release the request either way, or scroll-back would be stuck.
      pendingAnchorRef.current = null;
    } else {
      return;
    }

    // A short page can land without pushing the sentinel out of view. IntersectionObserver only
    // reports transitions, so ask for the next page directly rather than waiting for a scroll that
    // may never come.
    if (sentinelVisibleRef.current && canLoadMore) {
      requestOlderPage();
    }
  }, [canLoadMore, firstItemKey, isLoadingMore, requestOlderPage]);

  return {
    viewportRef: setViewport,
    sentinelRef: setSentinel,
    /** True while an older page is being fetched — drives the loading row above the oldest item. */
    isLoadingOlderMessages: isLoadingMore,
  };
}
